/**
 * What happened last time you were here.
 *
 * ## The claim
 *
 * Every journal can tell a trader how they did last month. None of them can
 * tell them how they do **in the situation currently on screen**, because
 * answering it needs three things at once: the market context now, the market
 * context at every past entry, and a definition of "the same situation" that
 * a trader would accept.
 *
 * AEGIS has the first two — `computeLayer1` is pure over bars, and regimes are
 * stamped at entry. This file is the third.
 *
 * ## A precedent is a conjunction, and conjunctions run out of data
 *
 * "ES, long, in a range, in the first hour, with volatility in the top third"
 * is a precise description of the setup and will match two trades. "ES" will
 * match four hundred and mean nothing. Neither is useful.
 *
 * So a query is a stack of facets, and the search **relaxes** — dropping the
 * least-defining facet until the sample is large enough to say something. What
 * it dropped is reported, because "8-6 in this regime" and "8-6 on ES at any
 * time in any regime" are different claims and the reader must be able to tell
 * which they are being given.
 *
 * ## Symbol never relaxes
 *
 * A precedent on a different instrument is not a precedent. If a trader has
 * four ES trades, the honest answer is "not enough", not "here is how you do
 * on crude".
 *
 * ## No model, and it cannot hallucinate
 *
 * This is Layer B: counting. The same reasoning as `personal.ts` — asking a
 * model to count someone's trades costs money per user, cannot be cached, and
 * would occasionally invent a number about real money the trader has every
 * reason to believe.
 */
import { sessionTemplate } from '../instruments/sessions.js';
import { marketRootOf, sessionTemplateIdFor } from '../instruments/registry.js';
import { NO_BAND, summarise, type BreakevenOptions, type Summary } from './analytics.js';
import { hourIn } from './personal.js';
import { UNCLASSIFIED, type RegimeStamp } from './scenario.js';
import type { Trade } from './types.js';

/**
 * The dimensions a precedent can match on.
 *
 * Listed in RELAXATION order — the first is dropped first. That ordering is
 * the whole design decision: hour is the finest and least defining, symbol is
 * the coarsest and the one thing that must always hold.
 */
export const FACET_ORDER = [
  'hour', 'session', 'volatility', 'regime', 'direction',
] as const;

export type FacetId = (typeof FACET_ORDER)[number] | 'symbol';

export interface Facet {
  readonly id: FacetId;
  readonly label: string;
  /** The value being matched. Compared as a string; never parsed. */
  readonly value: string;
  /** How to say it in a sentence: "in a range", "in the first hour". */
  readonly phrase: string;
}

/** Volatility as a band rather than a percentile — three buckets, not a hundred. */
export function volatilityBand(percentile: number | null | undefined): string | null {
  if (percentile === null || percentile === undefined || !Number.isFinite(percentile)) {
    return null;
  }
  if (percentile >= 66) return 'high';
  if (percentile <= 33) return 'low';
  return 'normal';
}

/**
 * Hour buckets, in the trader's own zone.
 *
 * Three-hour blocks rather than the hour itself. An hour is too fine — it
 * splits a session into eight buckets of three trades — and "morning" is too
 * coarse to mean anything on a futures day that runs twenty-three hours.
 */
export function hourBlock(hour: number): string {
  const start = Math.floor(hour / 3) * 3;
  return `${String(start).padStart(2, '0')}-${String(start + 3).padStart(2, '0')}`;
}

/** The situation being asked about. Everything is optional except the symbol. */
export interface Situation {
  readonly symbol: string;
  /** Absent when nothing is open — the mirror then covers both directions. */
  readonly direction?: 'long' | 'short';
  readonly regime?: string;
  readonly atrPercentile?: number;
  /** The moment being described. Defaults to now. */
  readonly at?: Date;
  /** The trader's own zone. Hour-of-day means nothing in the wrong one. */
  readonly timezone: string;
}

/** Whether a moment falls in the instrument's regular session. */
function inRth(symbol: string, at: Date): boolean {
  const tpl = sessionTemplate(sessionTemplateIdFor(symbol));
  if (!tpl.rth) return true;
  // Compared in the template's own zone, which is what `tradingDayFor` uses.
  const hhmm = new Intl.DateTimeFormat('en-GB', {
    timeZone: tpl.timezone, hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(at);
  return hhmm >= tpl.rth.start && hhmm < tpl.rth.end;
}

/** The facets of the situation on screen. */
export function situationFacets(s: Situation): readonly Facet[] {
  const at = s.at ?? new Date();
  const out: Facet[] = [{
    id: 'symbol', label: 'Instrument', value: s.symbol, phrase: `on ${s.symbol}`,
  }];

  if (s.direction) {
    out.push({
      id: 'direction', label: 'Direction', value: s.direction,
      phrase: s.direction === 'long' ? 'long' : 'short',
    });
  }
  if (s.regime && s.regime !== UNCLASSIFIED) {
    out.push({
      id: 'regime', label: 'Regime', value: s.regime,
      phrase: `in ${s.regime.replace(/[-_]/g, ' ')}`,
    });
  }
  const band = volatilityBand(s.atrPercentile);
  if (band) {
    out.push({
      id: 'volatility', label: 'Volatility', value: band,
      phrase: `with ${band} volatility`,
    });
  }
  const rth = inRth(s.symbol, at);
  out.push({
    id: 'session', label: 'Session', value: rth ? 'rth' : 'eth',
    phrase: rth ? 'in the regular session' : 'outside the regular session',
  });
  const block = hourBlock(hourIn(at, s.timezone));
  out.push({ id: 'hour', label: 'Time of day', value: block, phrase: `between ${block}` });

  return out;
}

/**
 * The facets of a past trade.
 *
 * Four come from the trade itself and need nothing stored. Regime and
 * volatility come from the stamp taken at entry — and a trade with no stamp
 * simply has no value for them, which makes it unmatchable on those facets
 * rather than wrongly matched.
 */
export function tradeFacets(
  t: Trade, stamps: ReadonlyMap<string, RegimeStamp>, timezone: string,
): ReadonlyMap<FacetId, string> {
  const m = new Map<FacetId, string>();
  // The MARKET, not the contract: MES trades are ES precedents, and treating
  // them as a different instrument splits one trader's history in half.
  m.set('symbol', marketRootOf(t.symbol));
  m.set('direction', t.direction);
  m.set('session', inRth(t.symbol, t.openedAt) ? 'rth' : 'eth');
  m.set('hour', hourBlock(hourIn(t.openedAt, timezone)));

  const stamp = stamps.get(t.id);
  if (stamp && stamp.regime !== UNCLASSIFIED) m.set('regime', stamp.regime);
  const band = volatilityBand(
    stamp?.atrPercentile === undefined ? null : Number(stamp.atrPercentile),
  );
  if (band) m.set('volatility', band);
  return m;
}

/** Why a mirror says nothing, when it says nothing. */
export type PrecedentReason = 'ok' | 'too_few_trades' | 'no_history';

export interface Precedent {
  readonly reason: PrecedentReason;
  /** Facets the sample actually matched on, symbol first. */
  readonly matched: readonly Facet[];
  /**
   * Facets dropped to reach a usable sample, in the order they were dropped.
   *
   * Reported because "8-6 in this regime" and "8-6 on ES at any time" are
   * different claims, and a reader given the second while believing the first
   * has been misled by omission.
   */
  readonly relaxed: readonly Facet[];
  readonly sampleSize: number;
  readonly summary: Summary;
  /** The matching trades, most recent first. For a caller that wants the list. */
  readonly trades: readonly Trade[];
  /** One descriptive sentence naming the sample and what it rests on. */
  readonly headline: string;
}

/**
 * Below this a precedent is a fact about three trades, not about the trader.
 *
 * Eight rather than `personal.ts`'s five, because a conjunction of facets
 * invites over-reading in a way a single statistic does not: "you are 5-1 in
 * this exact setup" reads as a discovered edge, and at that sample it is
 * noise wearing the costume of one.
 */
export const MIN_PRECEDENT = 8;

export interface PrecedentOptions {
  readonly minSample?: number;
  readonly breakeven?: BreakevenOptions;
  /** How many matching trades to return. The summary covers all of them. */
  readonly limit?: number;
}

const EMPTY_SUMMARY = summarise([]);

/**
 * How this trader has done in the situation on screen.
 *
 * Relaxes from the strictest match toward the loosest, stopping at the first
 * sample big enough to speak. Symbol never relaxes.
 */
export function precedent(
  trades: readonly Trade[],
  stamps: ReadonlyMap<string, RegimeStamp>,
  situation: Situation,
  opts: PrecedentOptions = {},
): Precedent {
  const minSample = opts.minSample ?? MIN_PRECEDENT;
  const facets = situationFacets(situation);
  const byId = new Map(facets.map((f) => [f.id, f]));

  const closed = trades.filter((t) => !t.isOpen);
  const facetsFor = new Map(
    closed.map((t) => [t.id, tradeFacets(t, stamps, situation.timezone)] as const),
  );

  const symbolFacet = byId.get('symbol')!;
  const root = marketRootOf(situation.symbol);
  const onSymbol = closed.filter((t) => facetsFor.get(t.id)?.get('symbol') === root);

  if (onSymbol.length === 0) {
    return {
      reason: 'no_history', matched: [symbolFacet], relaxed: [],
      sampleSize: 0, summary: EMPTY_SUMMARY, trades: [],
      headline: `No recorded ${situation.symbol} trades to compare this against.`,
    };
  }

  /*
    Relax from the strictest match down.

    `active` starts as every facet the situation could supply and loses one per
    round, in `FACET_ORDER`. The first sample that clears `minSample` wins, so
    the answer is always the most specific one that is still worth saying.
  */
  const relaxable = FACET_ORDER.filter((id) => byId.has(id));
  const relaxed: Facet[] = [];

  for (let drop = 0; drop <= relaxable.length; drop += 1) {
    const active = relaxable.slice(drop);
    const matching = onSymbol.filter((t) => {
      const tf = facetsFor.get(t.id)!;
      return active.every((id) => tf.get(id) === byId.get(id)!.value);
    });

    if (matching.length >= minSample || drop === relaxable.length) {
      const matched = [symbolFacet, ...active.map((id) => byId.get(id)!)];
      const enough = matching.length >= minSample;
      const ordered = [...matching].sort(
        (a, b) => (b.closedAt?.getTime() ?? 0) - (a.closedAt?.getTime() ?? 0),
      );
      return {
        reason: enough ? 'ok' : 'too_few_trades',
        matched,
        relaxed,
        sampleSize: matching.length,
        summary: summarise(matching, opts.breakeven ?? NO_BAND),
        trades: ordered.slice(0, opts.limit ?? 10),
        headline: headlineFor(matching.length, matched, relaxed, enough, minSample),
      };
    }
    relaxed.push(byId.get(relaxable[drop]!)!);
  }

  /* Unreachable: the loop returns on its final iteration. Kept so the function
     has one exit type rather than an implicit undefined. */
  return {
    reason: 'too_few_trades', matched: [symbolFacet], relaxed,
    sampleSize: 0, summary: EMPTY_SUMMARY, trades: [],
    headline: `Not enough ${situation.symbol} history to compare this against.`,
  };
}

/**
 * The sentence over the figures.
 *
 * Descriptive, and it names the sample and what was dropped in the same
 * breath. A reader who does not know the query was widened cannot weigh the
 * answer.
 */
function headlineFor(
  n: number, matched: readonly Facet[], relaxed: readonly Facet[],
  enough: boolean, minSample: number,
): string {
  const where = matched
    .filter((f) => f.id !== 'symbol')
    .map((f) => f.phrase)
    .join(', ');
  const symbol = matched[0]!.value;

  if (!enough) {
    return `Only ${n} recorded ${symbol} trade${n === 1 ? '' : 's'} — `
      + `fewer than the ${minSample} it takes to describe a pattern rather than a run.`;
  }

  const base = where
    ? `${n} recorded ${symbol} trades ${where}.`
    : `${n} recorded ${symbol} trades.`;

  if (relaxed.length === 0) return base;
  const dropped = relaxed.map((f) => f.label.toLowerCase()).join(', ');
  return `${base} Widened past ${dropped} to reach a sample worth reporting.`;
}
